import { Link } from "@tanstack/react-router";
import { Phone, Menu, X, Languages, Accessibility } from "lucide-react";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAccessibilityPanel } from "@/components/AccessibilityWidget";
import { pic } from "@/lib/responsive-images";

const logo = pic("logo-horizontal");

export function SiteHeader() {
  const { t, lang, setLang } = useI18n();
  const { setOpen: setA11yOpen } = useAccessibilityPanel();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const showConsultation = false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Solid (light) navbar when scrolled or mobile menu open. Transparent over the hero on every page.
  const solid = scrolled || open;

  const linkBase = solid
    ? "text-primary hover:text-accent"
    : "text-white/95 drop-shadow hover:text-accent";
  const langBtn = solid
    ? "border-border bg-background text-primary hover:border-accent hover:text-accent"
    : "border-white/40 bg-white/10 text-white backdrop-blur hover:border-accent hover:text-accent";
  const consultVariant = solid ? "outline" : "whiteOutline";

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        solid
          ? "bg-background/95 backdrop-blur-md shadow-sm"
          : "bg-transparent",
      )}
    >
      <div
        className={cn(
          "mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 transition-[height] duration-300",
          scrolled ? "h-20 py-2" : "h-24 py-3",
        )}
      >
        <Link to="/" className="flex h-full shrink-0 items-center" aria-label="AMR 3 Design & Construction LLC">
          <img
            src={logo.src}
            srcSet={logo.srcSet}
            sizes="140px"
            alt="AMR 3 Design & Construction LLC"
            className={cn(
              "w-auto transition-all duration-300",
              scrolled ? "h-14" : "h-20 sm:h-24",
              !solid && "drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]",
            )}
            width={140}
            height={140}
          />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-5 lg:flex xl:gap-7">
          {links({ t }).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={cn(
                "relative whitespace-nowrap text-sm font-medium transition-colors py-1",
                "after:pointer-events-none after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-accent after:transition-transform after:duration-300 hover:after:scale-x-100",
                linkBase,
              )}
              activeProps={{ className: "text-accent font-semibold after:scale-x-100" }}
              activeOptions={{ exact: l.to === "/" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <button
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors",
              langBtn,
            )}
            aria-label={lang === "en" ? "Cambiar a español" : "Switch to English"}
          >
            <Languages className="h-3.5 w-3.5" aria-hidden="true" />
            {lang === "en" ? "ES" : "EN"}
          </button>
          {showConsultation && (
            <Button asChild variant={consultVariant} size="default" className="hidden xl:inline-flex">
              <Link to="/contact" hash="project-form">{t("nav.requestConsultation")}</Link>
            </Button>
          )}
          <Button asChild variant="gold" size="default">
            <a href="tel:+15058680847" className="gap-2 whitespace-nowrap">
              <Phone className="h-4 w-4" /> {solid ? t("nav.callNow") : t("nav.callFull")}
            </a>
          </Button>
        </div>


        <button
          className={cn(
            "inline-flex h-11 w-11 items-center justify-center rounded-md lg:hidden",
            solid ? "text-primary" : "text-white",
          )}
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-border bg-background shadow-lg lg:hidden">
          <div className="flex flex-col gap-1 px-4 py-4">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setA11yOpen(true);
              }}
              className="mb-1 flex items-center gap-3 rounded-md bg-accent/10 px-3 py-3 text-left text-sm font-semibold text-primary"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <Accessibility className="h-4 w-4" aria-hidden />
              </span>
              {t("a11y.title")}
            </button>
            {links({ t }).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium text-primary hover:bg-secondary"
                activeProps={{
                  className: "bg-accent/10 text-accent font-semibold border-l-2 border-accent",
                }}
                activeOptions={{ exact: l.to === "/" }}
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <button
                onClick={() => setLang(lang === "en" ? "es" : "en")}
                className="rounded-full border border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-primary"
              >
                {lang === "en" ? "Español" : "English"}
              </button>
              {showConsultation && (
                <Button asChild variant="outline" size="lg">
                  <Link to="/contact" hash="project-form" onClick={() => setOpen(false)}>
                    {t("nav.requestConsultation")}
                  </Link>
                </Button>
              )}
              <Button asChild variant="gold" size="lg">
                <a href="tel:+15058680847">
                  <Phone className="h-4 w-4" /> {t("nav.callNow")}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function links({ t }: { t: (k: string) => string }) {
  return [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/about", label: t("nav.about") },
    { to: "/work", label: t("nav.work") },
    
    { to: "/faq", label: t("nav.faq") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contact", label: t("nav.contact") },
  ] as const;
}
